import {
  Box,
  Field,
  HStack,
  Input,
  InputProps,
  Link,
  Text,
  useDisclosure
} from "@chakra-ui/react"
import Avatar from "@pathwright/ui/src/components/avatar/Avatar"
import { getFormError } from "@pathwright/ui/src/components/form/utils"
import useDidMountEffect from "@pathwright/ui/src/components/hooks/useDidMountEffect"
import OutsideClickWrapper from "@pathwright/ui/src/components/overlay/OutsideClickWrapper"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import LazyQuillEditor from "@pathwright/ui/src/components/quill/LazyQuillEditor"
import { TOOLBAR_CONFIG_TYPES } from "@pathwright/ui/src/components/quill/toolbar/QuillToolbar"
import { useScreensizeContext } from "@pathwright/ui/src/components/ui/Screensize"
import { media } from "@pathwright/ui/src/components/utils/styles"
import { DarkMode } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import { toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import DiscardButton from "@pathwright/ux/src/components/form/DiscardButton"
import SubmitButton from "@pathwright/ux/src/components/form/SubmitButton"
import { FormikWithDiscardFormState } from "@pathwright/ux/src/components/form/state/FormikDiscardFormState"
import KeyboardFormSubmit from "@pathwright/ux/src/components/form/state/KeyboardFormSubmit"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import { FormikHelpers, FormikProps } from "formik"
import { Fragment, useRef, useState } from "react"
import styled from "styled-components"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import {
  CreateDiscussionMutationVariables,
  DiscussionContextInput,
  DiscussionPostFragment,
  useCohortPermissionsQuery,
  useCreateDiscussionMutation,
  useDiscussionPostLazyQuery,
  useUpdateDiscussionMutation
} from "@pathwright/graphql-client/src/generated"
import useCohortMembersStats from "../../cohort/useCohortMembersStats"
import useCohortPermissions from "../../cohort/useCohortPermissions"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import useTags from "../../tag/useTags"
import { DISCUSSION_TYPE_QUESTION } from "../constants"
import { useDiscussionListContext } from "../list/DiscussionListContainer"
import { getDiscussionContext } from "../list/item/utils"
import { invalidateDiscussionCache } from "../list/utils"
import useRefetchSyncPlanForDiscussion from "../useRefetchSyncPlanForDiscussion"
import DiscussionFormNotify from "./DiscussionFormNotify"
import DiscussionFormTags from "./DiscussionFormTags"
import { DISCUSSION_NOTIFY_OPTIONS } from "./constants"

const discussionSchema = z.object({
  title: z
    .string()
    .min(1, "Please enter a title.")
    .max(255, "Please enter a title that's no more than 255 characters."),
  body: z.string().nullable().optional(),
  isDiscussionQuestion: z.boolean(),
  selectedTags: z.array(
    z
      .object({
        name: z.string(),
        // Optional fields for when the tag already exists, allowing form to submit
        // when creating new tag (which will be automatically created during submit handler).
        id: z.number().nullable().optional(),
        slug: z.string().nullable().optional(),
        description: z.string().nullable().optional()
      })
      .nullable()
      .optional()
  ),
  notify: z.boolean().optional()
})

const validate = toFormikValidate(discussionSchema)

type DiscussionValues = z.infer<typeof discussionSchema>

// Scopes for state storage to a discussion or context.
// Deriving the context from the discussion or passed context.
export function getDiscussionFormStateKey({
  discussionContext,
  discussionId
}: {
  discussionContext: DiscussionContextInput
  discussionId?: number | null
}): [
  "discussion",
  number | null, // discussion id or null for new discussion
  DiscussionContextInput
] {
  const context = {
    resource_id: discussionContext.resource_id,
    cohort_id: discussionContext.cohort_id,
    step_id: discussionContext.step_id
  }

  return ["discussion", discussionId || null, context]
}

const useShouldEnableNotify = ({
  cohortId,
  discussionExists
}: {
  cohortId: number
  discussionExists: boolean
}) => {
  const cohortPermissionsQuery = useCohortPermissionsQuery({
    variables: {
      cohortId: cohortId!
    },
    skip: discussionExists
  })

  // Cannot notify about existing discussion, only new discussion.
  if (discussionExists) return false

  // Cannot notify about source cohort discussion.
  return cohortPermissionsQuery.data?.cohort?.is_source_cohort === false
}

const Container = styled.section<{ $expanded: boolean; $inverted: boolean }>`
  --inline-discussion-spacing: 1em;
  --inline-discussion-timing: 0.2s;

  margin: 0 auto;
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: ${(p) =>
    p.$expanded
      ? "var(--inline-discussion-spacing)"
      : "calc(var(--inline-discussion-spacing) / 2)"};
  background: ${(p) =>
    p.$inverted ? "rgba(255, 255, 255, 0.33)" : "tranparent"};
  border-radius: 10px;
  transition: padding var(--inline-discussion-timing) ease;
  cursor: ${(p) => (p.$expanded ? "initial" : "text")};

  /* ensure Avatar is visible on smaller screensizes */
  max-width: calc(100% - 70px);
  min-width: min(calc(100% - 70px), 100%);

  /* Avatar is hidden on phone screensize */
  ${(p) => (p.$expanded ? media.max.phone`max-width: 100%;` : "")}

  .DiscussionInlineForm__title {
    position: relative;
    display: flex;
    align-items: flex-start;

    .Avatar {
      position: absolute;
      left: calc(
        -1 * var(--avatar-size) - ${(p) => (p.$expanded ? "calc(var(--inline-discussion-spacing) / 2)" : "calc(var(--inline-discussion-spacing) / 4)")}
      );
      transition: left var(--inline-discussion-timing) ease;

      /* hide Avatar on small screens when expanded */
      ${(p) => (p.$expanded ? media.max.phone` display: none;` : "")}
    }
  }

  .UserTextInput,
  .RichTextInput {
    min-width: 100%;
  }

  ${(p) =>
    p.$inverted &&
    `.Avatar,
    .SubmitButton,
    .TagSelector {
      box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.15);
    }`}

  form {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    ${media.min
      .phone`padding-left: calc(var(--inline-discussion-spacing) / 2);`}
  }

  .TagSelector {
    margin-bottom: calc(var(--inline-discussion-spacing) / 2);
  }

  .TextInput {
    padding: 0;
    width: 100%;

    .TextInput__input input {
      ${(p) => !p.$expanded && "border-color: transparent"};
      transition: border-color var(--inline-discussion-timing) ease,
        background-color var(--inline-discussion-timing) ease;
    }

    .TextInput__input input[name="title"] {
      background-color: ${(p) => (p.$expanded ? "white" : "transparent")};
    }

    ${(p) =>
      p.$inverted &&
      `.styled-field > span {
      color: white;
    }`}
  }

  .ql-editor {
    /* roughly 2 lines, assuming line-height is 1.3em */
    min-height: 2.6em;
  }

  .ql-container {
    background: white;
  }
`

const ExpandedContainer = styled.div<{ $expanded: boolean }>`
  width: 100%;
  opacity: ${(p) => (p.$expanded ? 1 : 0)};
  transition: opacity var(--inline-discussion-timing) ease;

  &:not(:empty) {
    margin-top: calc(var(--inline-discussion-spacing) / 2);
  }
`

const SubmitActionContainer = styled.div`
  display: flex;
  align-items: center;
  /* If DiscussionFormNotify is not present, the SubmitActionContainer
    will fill the entire SubmitContainer and be centered. */
  flex-grow: 1;
  justify-content: center;

  > span {
    margin-left: 0.4em;
    margin-right: 0.4em;
  }
`

const SubmitContainer = styled.div`
  display: flex;
  align-items: center;
  min-width: 100%;
  margin-top: calc(var(--inline-discussion-spacing) / 2);
  ${media.max.phone`flex-direction: column;`}

  > .DiscussionFormNotify {
    /* Whenever DiscussionFormNotify is present, take up remaining space
      on the row */
    flex-grow: 10000000;
    ${media.max.phone`margin-bottom: var(--inline-discussion-spacing);`}
  }

  .Tooltip {
    min-width: 200px;
    white-space: normal;
    text-align: left;
  }
`

type DiscussionFormProps = {
  context?: DiscussionContextInput
  discussion?: DiscussionPostFragment | null
  isDiscussionQuestion?: boolean
  titleOptions?: {
    placeholder?: string
  }
  inverted?: boolean
  expanded?: boolean
  onToggleExpanded?: (expanded: boolean) => void
  onCancel?: () => void
  onSubmit?: (discussion: DiscussionPostFragment) => void
}

const discussionFormDefaultPropTitleOptions = {}
const discussionFormDefaultPropOnToggleExpanded = () => {}
const discussionFormDefaultPropOnCancel = () => {}
const discussionFormDefaultPropOnSubmit = () => {}

// TODO: DiscussionForm has become unweildy now that it supports updating an
// existing discussion. Would be great to separate concerns and dumb down the
// form a bit.
const DiscussionForm = ({
  context: contextProp,
  discussion,
  isDiscussionQuestion = false,
  titleOptions = discussionFormDefaultPropTitleOptions,
  inverted = true,
  expanded: preferredExpanded = false,
  onToggleExpanded = discussionFormDefaultPropOnToggleExpanded,
  onCancel = discussionFormDefaultPropOnCancel,
  onSubmit = discussionFormDefaultPropOnSubmit
}: DiscussionFormProps) => {
  const [showBody, setShowBody] = useState(preferredExpanded)
  const titleInputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslate()
  const pwContext = usePathwrightContext()
  const discussionListContext = useDiscussionListContext()
  const refetchSyncPlanForDiscussion = useRefetchSyncPlanForDiscussion()
  const screensize = useScreensizeContext()
  const [getDiscussionPostQuery] = useDiscussionPostLazyQuery()
  // discussion context can be supplied via prop or context
  const discussionContext =
    contextProp ||
    (discussionListContext
      ? discussionListContext.context
      : getDiscussionContext(discussion))
  const cohortMembersStats = useCohortMembersStats(discussionContext)
  const cohortPerms = useCohortPermissions({
    cohortId: discussionContext.cohort_id
  })
  const shouldEnableNotify = useShouldEnableNotify({
    cohortId: discussionContext.cohort_id!,
    discussionExists: !!discussion
  })

  const { open, onOpen, onClose } = useDisclosure({
    defaultOpen: preferredExpanded
  })

  function exitStatefulExpanded() {
    preferredExpanded ? onOpen() : onClose()
  }

  // Only interested in open when it has changed from initial state.
  useDidMountEffect(() => {
    onToggleExpanded && onToggleExpanded(open)
    if (open) titleInputRef.current?.focus()
  }, [open])

  const [createDiscussion] = useCreateDiscussionMutation({
    // HACK ALERT: https://medium.com/@martinseanhunt/how-to-invalidate-cached-data-in-apollo-and-handle-updating-paginated-queries-379e4b9e4698
    update: (cache) => {
      invalidateDiscussionCache(discussionListContext)
    },
    onCompleted: ({ createDiscussion }) => {
      if (createDiscussion) refetchSyncPlanForDiscussion(createDiscussion)
    }
  })

  const [updateDiscussion] = useUpdateDiscussionMutation()

  // The tags that initially exist, if discussion.
  const discussionTags =
    discussion?.tagLinks?.edges?.map((e) => e?.node?.tag) || []
  const { updateSelectedTags } = useTags({
    context: discussionContext,
    selectedTags: discussionTags
  })

  const refetchDiscussionPost = () => {
    if (!discussion) return
    return getDiscussionPostQuery({
      variables: {
        id: discussion.id,
        context: discussionContext
      },
      fetchPolicy: "network-only"
    })
  }

  const initialValues: DiscussionValues = discussion
    ? {
        title: discussion.title,
        body: discussion.body,
        isDiscussionQuestion: discussion.is_discussion_question,
        selectedTags: discussionTags || [],
        notify: DISCUSSION_NOTIFY_OPTIONS.DO_NOT_NOTIFY
      }
    : {
        title: "",
        body: "",
        isDiscussionQuestion,
        selectedTags: [],
        // notify may update automatically after contextual permissions
        // are applied in DiscussionFormNotify.
        notify: DISCUSSION_NOTIFY_OPTIONS.DO_NOT_NOTIFY
      }

  const handleSubmit = async (
    values: DiscussionValues,
    actions: FormikHelpers<DiscussionValues>
  ) => {
    const variables: Exclude<
      CreateDiscussionMutationVariables,
      "id" | "notify"
    > = {
      context: discussionContext,
      post: {
        title: values.title,
        body: values.body,
        is_discussion_question: values.isDiscussionQuestion,
        // Currently all step and community discussions are set as "question" types
        // regardless of isDiscussionQuestion.
        type: DISCUSSION_TYPE_QUESTION
      }
    }

    const mutatedDiscussion = discussion
      ? (
          await updateDiscussion({
            variables: {
              ...variables,
              id: discussion.id
            },
            optimisticResponse: {
              updateDiscussion: {
                __typename: "Discussion",
                ...discussion,
                ...variables
              }
            }
          })
        )?.data?.updateDiscussion
      : (
          await createDiscussion({
            variables: {
              ...variables,
              notify: values.notify
            }
          })
        )?.data?.createDiscussion

    if (mutatedDiscussion) {
      // Select the tags now that the discussion has been created.
      await updateSelectedTags(values.selectedTags, {
        discussion_context_id: mutatedDiscussion?.context_id
      })

      if (discussion) {
        // Refetching discussion ensures tag links are updated in cache.
        // TODO: only refetch if selected tags changed.
        if (values.selectedTags?.length) {
          await refetchDiscussionPost()
        }
      }

      if (discussionListContext) {
        // reset discussion list sorting and filtering to their defaults
        const { initialSort, initialFilter, onSort, onFilter, refetch } =
          discussionListContext

        onSort(initialSort)
        onFilter(initialFilter)
        // refetch the default list
        await refetch()
      }

      // Only reset to initial state if we created a discussion.
      if (!discussion) {
        // reset form and collapse after successful submission (TODO: ensure successful)
        actions.resetForm({
          values: initialValues
        })
        exitStatefulExpanded()
        // alert user to the fact that they notified x number of people
        if (values.notify === DISCUSSION_NOTIFY_OPTIONS.NOTIFY) {
          toaster.create({
            title: t("{{ count, number }} person was notified.", {
              defaultValue_plural: "{{ count, number }} people were notified.",
              count: cohortMembersStats.send_discussion_notifications
            }),
            type: "success",
            duration: 5000,
            closable: true
            // onCloseComplete: onClear,
          })
        }
      }

      // Alert listeners of discussion being updated/created.
      const eventName = discussion ? "discussion:updated" : "discussion:created"
      const event = new CustomEvent(eventName, {
        detail: { discussion: mutatedDiscussion }
      })
      document.dispatchEvent(event)

      onSubmit?.(mutatedDiscussion)
    }
  }

  const titleInputIsInverted = inverted && !open

  const InvertedWrapper = inverted ? DarkMode : Fragment

  // When body editor is empty it contains this value:
  const emptyBodyValue = "<p><br></p>"

  const formStateKey = getDiscussionFormStateKey({
    discussionContext,
    discussionId: discussion?.id
  })

  function getInputProps(inverted: boolean): InputProps {
    return {
      size: "lg", // NOTE: this size refers to density, not font size. Font size will be one level smaller.
      background: inverted ? "transparent" : "white",
      outline: "none",
      // outlineWidth: "1px",
      _hover: {
        outline: "none"
      },
      _placeholder: {
        color: inverted ? "white" : "{colors.gray.900/50}"
      },
      px: "1",
      borderColor: inverted ? "{colors.gray.100/50}" : "{colors.gray.900/20}",
      borderWidth: inverted ? "0px" : "1px"
    }
  }

  if (!pwContext.me || !cohortPerms.hasLearnerLevelAccess) return null

  return (
    <FormikWithDiscardFormState
      formStateKey={formStateKey}
      initialValues={initialValues}
      validate={(...args) => (open ? validate(...args) : undefined)}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(form: FormikProps<DiscussionValues>) => {
        const titleIsInvalid =
          open &&
          Object.keys(form.touched).length &&
          !!getFormError(form, "title", false)

        return (
          <>
            <OutsideClickWrapper
              shouldListen={open}
              onOutsideClick={() => {
                // OK to clear all errors as the only validation performed is to
                // check if the title has a value
                form.setErrors(
                  Object.keys(form.errors).reduce(
                    (errors, key) => ({
                      ...errors,
                      [key]: null
                    }),
                    {}
                  )
                )

                // only collapse UIs if not interacting with some related, yet not nested, UIs
                // hacky, but no ideal way to know that the QuillEditor extended UI is being interacted with
                if (
                  !document.querySelector(".TagManagerCard") &&
                  !document.querySelector(".EmbedCard") &&
                  !document.querySelector("#__filestack-picker")
                ) {
                  const bodyIsEmpty =
                    !form.values.body || form.values.body === emptyBodyValue
                  // for now, only collapse if the form is untouched
                  if (
                    !form.values.title &&
                    bodyIsEmpty &&
                    !form.values.selectedTags.length
                  )
                    exitStatefulExpanded()
                  // hiding the QuillEditor if has no value
                  if (bodyIsEmpty) setShowBody(preferredExpanded)
                }
              }}
            >
              <Container
                className="DiscussionForm"
                role="application"
                aria-expanded={open}
                $expanded={open}
                $inverted={inverted}
                onClick={() => onOpen()}
              >
                <form onSubmit={form.handleSubmit}>
                  <KeyboardFormSubmit />
                  {open && (
                    <DiscussionFormTags
                      context={{
                        ...discussionContext,
                        discussion_context_id: discussion?.context_id ?? null
                      }}
                      selectedTags={form.values.selectedTags}
                      onChange={(selectedTags) =>
                        form.setFieldValue("selectedTags", selectedTags)
                      }
                      onTagsLinked={() => refetchDiscussionPost()}
                    />
                  )}
                  <div className="DiscussionInlineForm__title">
                    {
                      // In order to show user's Avatar, must not be a "discussion prompt",
                      // and user either must be creating a new discussion or editing their own discussion.
                      !form.values.isDiscussionQuestion &&
                        (!discussion ||
                          discussion.user?.id === pwContext.me.id) && (
                          <Avatar
                            user={pwContext.me}
                            size={screensize === "sm" ? 30 : 40}
                          />
                        )
                    }
                    <Field.Root
                      // Only show error if body field has been touched.
                      invalid={!!titleIsInvalid}
                    >
                      <Input
                        ref={titleInputRef}
                        type="text"
                        id="discussion-title"
                        name="title"
                        // variant="filled"
                        // isRequired
                        placeholder={
                          // TODO: cleanup logic.
                          // Using prompt from a selected tag when only that tag is selected and has a "prompt" (description).
                          (form.values.selectedTags.length === 1 &&
                            form.values.selectedTags[0]?.description) ||
                          titleOptions?.placeholder ||
                          t("What would you like to discuss?")
                        }
                        value={form.values.title}
                        onChange={(e) => form.handleChange(e)}
                        onBlur={(e) => form.handleBlur(e)}
                        // Autofocusing when not autofocusing the body.
                        autoFocus={!!form.values.body && !preferredExpanded}
                        {...getInputProps(titleInputIsInverted)}
                      />
                      {titleIsInvalid ? (
                        <Field.ErrorText
                          color={inverted ? "white" : "{colors.gray.900/50}"}
                        >
                          <HStack>
                            <Pathicon icon="x" size="1em" />
                            <span>
                              {getFormError(form, "title", false)?.[0]}
                            </span>
                          </HStack>
                        </Field.ErrorText>
                      ) : null}
                    </Field.Root>
                  </div>
                  <ExpandedContainer $expanded={open}>
                    {open &&
                      (showBody ? (
                        <LazyQuillEditor
                          name="body"
                          html={form.values.body}
                          onChange={(html: string) =>
                            form.setFieldValue(
                              "body",
                              html,
                              false /* shouldValidate */
                            )
                          }
                          onBlur={() => form.setFieldTouched("body")}
                          onDelete={() => {
                            if (
                              !form.values.body ||
                              form.values.body === emptyBodyValue
                            )
                              setShowBody(preferredExpanded)
                          }}
                          // Autofocus the QuillEditor when it mounts as long as body has no value
                          // and we aren't mounting the DiscussionForm in its expanded state.
                          autofocus={
                            (!form.values.body ||
                              form.values.body === emptyBodyValue) &&
                            !preferredExpanded
                          }
                          toolbar={TOOLBAR_CONFIG_TYPES.FULL}
                          createCustomToolbar={({
                            getMergedToolbarConfig
                          }: any) =>
                            getMergedToolbarConfig({
                              excludedOptions: "header"
                            })
                          }
                          theme="snow"
                          controlled
                        />
                      ) : (
                        <Input
                          placeholder={t(
                            "Add more information, images, etc. (optional)"
                          )}
                          onFocus={() => setShowBody(true)}
                          {...getInputProps(inverted)}
                          // Always show border for this Input.
                          borderWidth="1px"
                        />
                      ))}
                    {open && (
                      <SubmitContainer>
                        {shouldEnableNotify && (
                          <DiscussionFormNotify
                            context={discussionContext}
                            inverted={inverted}
                          />
                        )}
                        <SubmitActionContainer>
                          <HStack
                            flexDir={preferredExpanded ? "row" : "row-reverse"}
                          >
                            {/* TODO: refactor this Tooltip idea to be part of the SubmitButton (or TooltipSubmitButton)
                            where all errors are listed in the Tooltip (since we're basically doing that here). */}
                            <Tooltip
                              fitContent
                              title={
                                !form.dirty || !form.isValid
                                  ? t("Please enter a title.")
                                  : ""
                              }
                            >
                              <Box>
                                <InvertedWrapper>
                                  <SubmitButton size="lg" w="auto" minW="unset">
                                    {discussion ? t("Update") : t("Post")}
                                  </SubmitButton>
                                </InvertedWrapper>
                              </Box>
                            </Tooltip>
                            <Text
                              as="span"
                              color={inverted ? "white" : "inherit"}
                            >
                              {t("or")}
                            </Text>
                            <Link asChild variant="underline">
                              <DiscardButton
                                variant="plain"
                                colorPalette={inverted ? "whiteAlpha" : "brand"}
                                color={inverted ? "whiteAlpha.800" : "brand"}
                                p={0}
                                background="none"
                                w="auto"
                                minW="unset"
                                onClick={() => {
                                  exitStatefulExpanded()
                                  onCancel?.()
                                }}
                              />
                            </Link>
                          </HStack>
                        </SubmitActionContainer>
                      </SubmitContainer>
                    )}
                  </ExpandedContainer>
                </form>
              </Container>
            </OutsideClickWrapper>
          </>
        )
      }}
    </FormikWithDiscardFormState>
  )
}

DiscussionForm.displayName = "DiscussionForm"

export default DiscussionForm
